import { ZuruflexApiError } from '@zuruflex/api-client';
import {
  Alert,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  FormField,
  RatingInput,
  Textarea,
} from '@zuruflex/ui';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../lib/api.js';

export interface GuestReviewDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  booking: { reference: string; guestName: string; vehicle: string };
}

/**
 * The owner's review of their guest, asked where the handover just happened.
 *
 * A dialog because a rating cannot be typed into a prompt, and because this
 * form has to carry a sentence about the review being public before anybody
 * presses send.
 */
export function GuestReviewDialog({ open, onOpenChange, booking }: GuestReviewDialogProps) {
  const { t } = useTranslation(['partner', 'common']);
  const queryClient = useQueryClient();

  const [overall, setOverall] = useState<number | null>(null);
  const [body, setBody] = useState('');
  const [tried, setTried] = useState(false);

  const submit = useMutation({
    mutationFn: () =>
      api.reviewGuest(booking.reference, {
        overall: overall ?? 0,
        ...(body.trim() ? { body: body.trim() } : {}),
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['my-bookings'] });
      void queryClient.invalidateQueries({ queryKey: ['partner-reviews'] });
      void queryClient.invalidateQueries({ queryKey: ['guests-to-review'] });
      onOpenChange(false);
      setOverall(null);
      setBody('');
      setTried(false);
    },
  });

  const labels = [1, 2, 3, 4, 5].map((n) => t(`common:rating.scale.${n}`)) as [
    string,
    string,
    string,
    string,
    string,
  ];
  const missing = tried && !overall;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('reviews.dialogTitle', { guest: booking.guestName })}</DialogTitle>
          <DialogDescription>
            {t('reviews.dialogBody', { vehicle: booking.vehicle })}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5">
          <FormField
            label={t('reviews.overall')}
            error={missing ? t('reviews.overallRequired') : undefined}
          >
            {(field) => (
              <RatingInput
                id={field.id}
                value={overall}
                onChange={setOverall}
                labels={labels}
                aria-describedby={field['aria-describedby']}
                aria-invalid={missing}
              />
            )}
          </FormField>

          <FormField label={t('reviews.comment')} hint={t('reviews.commentHint')}>
            {(field) => (
              <Textarea
                id={field.id}
                rows={4}
                maxLength={2000}
                value={body}
                onChange={(event) => setBody(event.target.value)}
                placeholder={t('reviews.commentPlaceholder')}
              />
            )}
          </FormField>

          {/*
            Said before the button, not after. This goes on a page with the
            guest's name on it, under the business name, and they can read it
            — an owner who finds that out afterwards has been told too late.
          */}
          <Alert variant="info">
            {t('reviews.publicNotice', { guest: booking.guestName })}
          </Alert>

          {submit.isError ? (
            <Alert variant="danger">
              {submit.error instanceof ZuruflexApiError
                ? submit.error.message
                : t('common:error.unexpected')}
            </Alert>
          ) : null}
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common:action.cancel')}
          </Button>
          <Button
            isLoading={submit.isPending}
            onClick={() => {
              setTried(true);
              if (overall) submit.mutate();
            }}
          >
            {t('reviews.send')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
